// Authentication service - connects to .NET backend API
// Backend: AuthController.cs with JWT authentication

const API_BASE_URL = `${process.env.REACT_APP_API_URI}/auth`;

/**
 * Helper function to handle API responses
 */
const handleResponse = async (response) => {
  const data = await response.json();
  
  if (!response.ok) {
    // API returns { success: false, message: "...", errors: [...] }
    // OR ASP.NET validation error format { errors: {...}, title: "..." }
    // The full body can carry whatever the API chose to echo back, so it is a
    // development aid only. Production keeps the status line and nothing else.
    if (process.env.NODE_ENV !== 'production') {
      console.error('[AUTH API ERROR]', {
        status: response.status,
        statusText: response.statusText,
        message: data.message,
        errors: data.errors,
        fullResponse: data,
      });
    } else {
      console.error('[AUTH API ERROR]', response.status, response.statusText);
    }
    
    // Extract validation errors if present
    let errorMessage = data.message || data.title || 'An error occurred';
    if (data.errors) {
      // ASP.NET Core validation format: { "FieldName": ["error1", "error2"] }
      const errorMessages = [];
      for (const messages of Object.values(data.errors)) {
        if (Array.isArray(messages)) {
          errorMessages.push(...messages);
        }
      }
      if (errorMessages.length > 0) {
        errorMessage = errorMessages.join('; ');
      }
    }
    
    throw new Error(errorMessage);
  }
  
  // API returns { success: true, message: "...", data: {...} }
  return data.data;
};

/**
 * Login user with email and password
 * Connects to: POST /api/auth/login
 * 
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<object>} - User object and token
 */
export const login = async (email, password) => {
  
  const response = await fetch(`${API_BASE_URL}/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password, rememberMe: false }),
  });
  
  const data = await handleResponse(response);
  
  // API returns: { token, refreshToken, tokenExpiration, user }
  return {
    user: data.user,
    token: data.token
  };
};

/**
 * Register new user
 * Connects to: POST /api/auth/register
 * 
 * @param {object} userData - User registration data
 * @returns {Promise<object>} - Created user and token
 */
export const register = async (userData) => {
  
  const response = await fetch(`${API_BASE_URL}/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email: userData.email,
      password: userData.password,
      firstName: userData.firstName,
      lastName: userData.lastName,
      username: userData.username || null,
      phoneNumber: userData.phoneNumber || null,
      zipCode: userData.zipCode || null,
      need1: userData.need1 || null,
      need2: userData.need2 || null,
      need3: userData.need3 || null,
      profilePicture: userData.profilePicture || null,
      identity: userData.identity || null,
      newsletterOptIn: userData.newsletterOptIn || false,
      termsAccepted: userData.termsAccepted || false,
      smsConsent: userData.smsConsent || false,
      smsConsentVersion: userData.smsConsentVersion || null,
      smsConsentSource: userData.smsConsentSource || null
    }),
  });
  
  const data = await handleResponse(response);
  
  return {
    user: data.user,
    token: data.token
  };
};

/**
 * Social authentication (Google/Facebook)
 * POST /api/auth/social
 *
 * @param {string} provider - 'google' or 'facebook'
 * @param {string} token - access token from the OAuth provider SDK
 * @param {object|null} completionData - required fields for new user registration
 * @returns {Promise<{user, token}|{isNewUser: true, providerProfile}>}
 */
export const socialAuth = async (provider, token, completionData = null) => {
  const response = await fetch(`${API_BASE_URL}/social`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider, token, completionData: completionData || null }),
  });

  const data = await handleResponse(response);

  if (data.isNewUser) {
    return { isNewUser: true, providerProfile: data.providerProfile };
  }
  return { user: data.user, token: data.token };
};

/**
 * Verify authentication token
 * Connects to: POST /api/auth/verify
 * 
 * @param {string} token - JWT token
 * @returns {Promise<object>} - User object if valid
 */
export const verifyToken = async (token) => {
  
  const response = await fetch(`${API_BASE_URL}/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(token),
  });
  
  const data = await handleResponse(response);
  
  // API returns UserDto
  return data;
};

/**
 * Get current user profile
 * Connects to: GET /api/auth/me
 * 
 * @param {string} token - JWT token
 * @returns {Promise<object>} - User object
 */
export const getCurrentUser = async (token) => {
  
  const response = await fetch(`${API_BASE_URL}/me`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  
  const data = await handleResponse(response);
  return data;
};

/**
 * Update user profile
 * Connects to: PUT /api/auth/profile
 * 
 * @param {string} token - JWT token
 * @param {object} updates - Profile updates
 * @returns {Promise<object>} - Updated user
 */
export const updateProfile = async (token, updates) => {
  
  const response = await fetch(`${API_BASE_URL}/profile`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });
  
  const data = await handleResponse(response);
  return data;
};

/**
 * Change password
 * Connects to: POST /api/auth/change-password
 * 
 * @param {string} token - JWT token
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise<boolean>} - Success status
 */
export const changePassword = async (token, currentPassword, newPassword) => {
  
  const response = await fetch(`${API_BASE_URL}/change-password`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  
  await handleResponse(response);
  return true;
};

/**
 * Request password reset
 * TODO: Implement password reset email flow
 * 
 * @param {string} email - User email
 * @returns {Promise<boolean>} - Success status
 */
export const requestPasswordReset = async (email) => {
  
  // TODO: Implement POST /api/auth/forgot-password endpoint
  // For now, return mock response
  throw new Error('Password reset not yet implemented. Please contact support.');
};

/**
 * Hard-delete the signed-in account (workspaces + story + identifying fields).
 * POST /api/auth/delete-account
 */
export const deleteAccount = async (token, confirmationEmail) => {
  const response = await fetch(`${API_BASE_URL}/delete-account`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ confirmationEmail }),
  });
  await handleResponse(response);
  return true;
};

/**
 * Download a copy of the signed-in user's data.
 * GET /api/auth/export
 */
export const exportAccount = async (token) => {
  const response = await fetch(`${API_BASE_URL}/export`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error('Could not download your data just now.');
  }
  return response.json();
};

/**
 * Logout user
 * Connects to: POST /api/auth/logout
 * 
 * @param {string} token - JWT token
 * @returns {Promise<boolean>} - Success status
 */
export const logout = async (token) => {
  
  try {
    await fetch(`${API_BASE_URL}/logout`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
  } catch (error) {
    // Logout locally even if server request fails
  }
  
  return true;
};

